import { t } from '../i18n/i18n';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createDesign } from '../engine/model/design';
import { hexRgb } from '../imaging/color';
import { type DecodedImage, ImportError, decodeImageFile } from '../imaging/decode';
import type { Analysis } from '../imaging/digitize';
import { type DigitizeOptions, defaultDigitizeOptions } from '../imaging/options';
import { type CropRect, crop as cropRaster } from '../imaging/raster';
import { addObjects } from '../state/designOps';
import { store, useApp } from '../state/store';
import { CancelledError, type DigitizeJob, startAnalyze, startDigitize } from '../workers/client';
import { setImage } from './actions';
import { IconClose, IconDown, IconImport, IconUp } from './icons';
import { NumberField } from './NumberField';

type Stage = 'pick' | 'prepare' | 'running';

interface PaletteEdit {
  hex: string[];
  share: number[];
  /** palette index -> target index (merge) */
  remap: number[];
  excluded: Set<number>;
  order: number[];
}

export function ImportDialog() {
  const [stage, setStage] = useState<Stage>('pick');
  const [decoded, setDecoded] = useState<DecodedImage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const design = useApp((s) => s.history.present);
  const close = () => store.set({ panel: null });

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    setError(null);
    setBusy(true);
    try {
      const d = await decodeImageFile(f);
      setDecoded(d);
      setStage('prepare');
    } catch (e) {
      setError(e instanceof ImportError ? e.message : t('The image could not be read: {error}', { error: e instanceof Error ? e.message : String(e) }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="import-title" onKeyDown={(e) => e.key === 'Escape' && stage !== 'running' && close()}>
      <div className="modal">
        <div className="modal-head">
          <h2 id="import-title">{t('Import image')}</h2>
          <button className="btn icon ghost" aria-label={t('Close dialog')} onClick={close} disabled={stage === 'running'}>
            <IconClose />
          </button>
        </div>
        {stage === 'pick' || !decoded ? (
          <>
            <div className="modal-body">
              <DropZone onFile={onFile} busy={busy} />
              {error && (
                <p className="issue error" role="alert" style={{ marginTop: 12 }}>
                  {error}
                </p>
              )}
              <p className="hint" style={{ marginTop: 12 }}>
                {t('PNG, JPEG or SVG, max 25 MB. The image is processed only in your browser and is never uploaded. Transparency in PNG and SVG is used as background.')}
              </p>
            </div>
            <div className="modal-foot">
              <button className="btn" onClick={close}>
                {t('Cancel')}
              </button>
            </div>
          </>
        ) : (
          <Prepare decoded={decoded} hasObjects={design.objects.length > 0} onBack={() => setStage('pick')} onRunning={(r) => setStage(r ? 'running' : 'prepare')} />
        )}
      </div>
    </div>
  );
}

function DropZone({ onFile, busy }: { onFile: (f: File | undefined) => void; busy: boolean }) {
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  return (
    <div
      className={'dropzone' + (over ? ' over' : '')}
      role="button"
      tabIndex={0}
      aria-label={t('Choose or drop an image file')}
      onClick={() => input.current?.click()}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && input.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        onFile(e.dataTransfer.files[0]);
      }}
    >
      <IconImport size={28} />
      <strong>{busy ? t('Reading the image…') : t('Drop an image here, or click to choose')}</strong>
      PNG · JPEG · SVG
      <input ref={input} type="file" accept="image/png,image/jpeg,image/svg+xml,.svg" hidden onChange={(e) => onFile(e.target.files?.[0])} data-testid="file-input" />
    </div>
  );
}

function Prepare({ decoded, hasObjects, onBack, onRunning }: { decoded: DecodedImage; hasObjects: boolean; onBack: () => void; onRunning: (r: boolean) => void }) {
  const img = decoded.raster;
  const [crop, setCrop] = useState<CropRect>({ x: 0, y: 0, width: img.width, height: img.height });
  const [opts, setOpts] = useState<DigitizeOptions>(() => ({ ...defaultDigitizeOptions(), widthMm: Math.min(90, store.design.machine.hoop.widthMm - 10) }));
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [edit, setEdit] = useState<PaletteEdit | null>(null);
  const [fixedPalette, setFixedPalette] = useState<string[] | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [progress, setProgress] = useState<{ phase: string; f: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'replace' | 'add'>(hasObjects ? 'replace' : 'replace');
  const job = useRef<DigitizeJob<unknown> | null>(null);
  const url = useMemo(() => URL.createObjectURL(new Blob([decoded.bytes as BlobPart], { type: decoded.mime })), [decoded]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);

  const cropped = useMemo(() => (crop.x === 0 && crop.y === 0 && crop.width === img.width && crop.height === img.height ? img : cropRaster(img, crop)), [img, crop]);

  // analysis (debounced, cancellable)
  const analysisKey = JSON.stringify([crop, opts.colorCount, opts.removeBackground, opts.backgroundTolerance, opts.alphaThreshold, opts.maxWorkingSize, fixedPalette]);
  useEffect(() => {
    setAnalyzing(true);
    setError(null);
    const t = setTimeout(() => {
      const j = startAnalyze(cropped, { ...opts, palette: fixedPalette ?? undefined });
      job.current = j as DigitizeJob<unknown>;
      j.promise
        .then((a) => {
          setAnalysis(a);
          setEdit((prev) =>
            fixedPalette && prev && prev.hex.length === a.palette.length
              ? { ...prev, share: a.shares }
              : { hex: a.palette, share: a.shares, remap: a.palette.map((_, i) => i), excluded: new Set(), order: a.palette.map((_, i) => i) },
          );
          setAnalyzing(false);
        })
        .catch((e) => {
          if (e instanceof CancelledError) return;
          setError(e instanceof Error ? e.message : String(e));
          setAnalyzing(false);
        });
    }, 250);
    return () => {
      clearTimeout(t);
      job.current?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisKey, cropped]);

  const heightMm = analysis ? (opts.widthMm * analysis.content.height) / analysis.content.width : (opts.widthMm * crop.height) / crop.width;

  const run = () => {
    if (!analysis || !edit) return;
    onRunning(true);
    setError(null);
    setProgress({ phase: 'Starter', f: 0 });
    const j = startDigitize(
      cropped,
      { ...opts, palette: edit.hex, remap: edit.remap, excluded: [...edit.excluded], colorOrder: edit.order.filter((i) => edit.remap[i] === i) },
      (phase, f) => setProgress({ phase, f }),
    );
    job.current = j as DigitizeJob<unknown>;
    j.promise
      .then((r) => {
        const mmPerPx = r.widthMm / r.analysis.content.width;
        const backdrop = {
          imageId: 'main',
          x: -(crop.x + r.analysis.content.x) * mmPerPx,
          y: -(crop.y + r.analysis.content.y) * mmPerPx,
          widthMm: img.width * mmPerPx,
          heightMm: img.height * mmPerPx,
          opacity: 0.5,
        };
        const base = mode === 'add' ? store.design : createDesign({ name: store.getState().project.name || decoded.name.replace(/\.[^.]+$/, ''), machine: store.design.machine });
        const palette = mode === 'add' ? [...base.palette, ...r.palette] : r.palette;
        const next = addObjects({ ...base, palette, widthMm: r.widthMm, heightMm: r.heightMm, backdrop }, r.objects);
        setImage(decoded);
        store.commitDesign(next);
        store.set((s) => ({ panel: null, selection: [], mode: 'edit', fitNonce: s.fitNonce + 1, layers: { ...s.layers, image: false, stitches: true } }));
        store.toast('success', t('Digitized: {n} objects in {c} colours.', { n: r.objects.length, c: r.palette.length }) + (r.mergedRegions ? ' ' + t('{n} small areas were merged.', { n: r.mergedRegions }) : ''));
      })
      .catch((e) => {
        onRunning(false);
        setProgress(null);
        if (e instanceof CancelledError) return;
        setError(e instanceof Error ? e.message : String(e));
      });
  };

  const importVector = async () => {
    setError(null);
    try {
      const { importSvg } = await import('../imaging/svg');
      const r = importSvg(new TextDecoder().decode(decoded.bytes), { widthMm: opts.widthMm, satinMinWidthMm: 1 });
      const base = mode === 'add' ? store.design : createDesign({ name: store.getState().project.name || decoded.name.replace(/\.[^.]+$/, ''), machine: store.design.machine });
      const palette = mode === 'add' ? [...base.palette, ...r.palette] : r.palette;
      setImage(decoded);
      store.commitDesign(addObjects({ ...base, palette, widthMm: r.widthMm, heightMm: r.heightMm, backdrop: mode === 'add' ? base.backdrop : null }, r.objects));
      store.set((s) => ({ panel: null, selection: [], mode: 'edit', fitNonce: s.fitNonce + 1, layers: { ...s.layers, stitches: true } }));
      store.toast('success', t('Imported {n} vector shapes in {c} colours.', { n: r.objects.length, c: r.palette.length }) + (r.warnings.length ? ' ' + r.warnings.join(' ') : ''), r.warnings.length ? 12000 : 5000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const useAsBackdrop = () => {
    const mmPerPx = opts.widthMm / crop.width;
    setImage(decoded);
    store.commitDesign({
      ...store.design,
      backdrop: { imageId: 'main', x: -crop.x * mmPerPx, y: -crop.y * mmPerPx, widthMm: img.width * mmPerPx, heightMm: img.height * mmPerPx, opacity: 0.6 },
    });
    store.set((s) => ({ panel: null, tool: 'polygon', fitNonce: s.fitNonce + 1, layers: { ...s.layers, image: true } }));
    store.toast('info', t('The image was added as a background. Draw shapes on top with the tools (polygon, line, rectangle …).'));
  };

  const set = <K extends keyof DigitizeOptions>(k: K, v: DigitizeOptions[K]) => setOpts((o) => ({ ...o, [k]: v }));

  return (
    <>
      <div className="modal-body">
        <div className="import-grid">
          <div>
            <div className="preview-pair">
              <CropView url={url} width={img.width} height={img.height} crop={crop} onCrop={setCrop} />
              <div className="preview-box">
                <span className="label chip">{analyzing ? t('Analysing…') : t('Colour separation')}</span>
                {analysis && edit && <QuantPreview analysis={analysis} edit={edit} />}
              </div>
            </div>
            <div className="row wrap" style={{ marginTop: 8 }}>
              <span className="hint" style={{ margin: 0 }}>
                {decoded.name} · {img.width} × {img.height} px · {t('crop')} {Math.round(crop.width)} × {Math.round(crop.height)} px
              </span>
              <button className="btn small ghost" onClick={() => setCrop({ x: 0, y: 0, width: img.width, height: img.height })}>
                {t('Reset crop')}
              </button>
            </div>
            {error && (
              <p className="issue error" role="alert" style={{ marginTop: 10 }}>
                {error}
              </p>
            )}
          </div>
          <div>
            {decoded.kind === 'svg' && (
              <div className="section" style={{ padding: '0 0 10px' }}>
                <h3>{t('SVG – vector import')}</h3>
                <p className="hint">{t('The shapes in the SVG file are imported directly as editable curves: filled shapes become fill, and strokes become running stitch or satin. This gives the most precise edges. The colour settings below apply only to pixel-based digitizing.')}</p>
                <button className="btn primary" onClick={() => void importVector()}>
                  {t('Import as vector shapes')}
                </button>
              </div>
            )}
            <div className="section" style={{ padding: '0 0 10px' }}>
              <h3>{t('Size')}</h3>
              <div className="grid2">
                <NumberField label={t('Width')} limit={{ min: 5, max: 1000, step: 1, unit: 'mm' }} value={opts.widthMm} onChange={(v) => set('widthMm', v)} />
                <NumberField label={t('Height')} limit={{ min: 5, max: 1000, step: 1, unit: 'mm' }} value={heightMm} onChange={(v) => set('widthMm', (v * opts.widthMm) / heightMm)} />
              </div>
              <p className="hint">{t('Applies to the visible motif (background and transparency removed). The aspect ratio is kept.')}</p>
            </div>
            <div className="section" style={{ padding: '10px 0' }}>
              <h3>{t('Colours and background')}</h3>
              <div className="grid2">
                <NumberField label={t('Number of colours')} decimals={0} limit={{ min: 1, max: 24, step: 1 }} value={opts.colorCount} onChange={(v) => { setFixedPalette(null); set('colorCount', Math.round(v)); }} />
                <NumberField label={t('Smallest detail')} limit={{ min: 0.2, max: 10, step: 0.1, unit: 'mm' }} value={opts.minDetailMm} onChange={(v) => set('minDetailMm', v)} />
              </div>
              <label className="check">
                <input type="checkbox" checked={opts.removeBackground} onChange={(e) => set('removeBackground', e.target.checked)} /> {t('Remove solid background')}
                {analysis?.background && <span className="swatch" style={{ background: analysis.background }} title={t('Removed: {colour}', { colour: analysis.background })} />}
              </label>
              {analysis && edit && (
                <PaletteEditor
                  edit={edit}
                  setEdit={setEdit}
                  onRecolor={(hex) => {
                    setFixedPalette(hex);
                    setEdit((e) => (e ? { ...e, hex } : e));
                  }}
                />
              )}
            </div>
            <div className="section" style={{ padding: '10px 0' }}>
              <h3>{t('Stitch choices')}</h3>
              <div className="grid2">
                <NumberField label={t('Smoothing')} decimals={0} limit={{ min: 0, max: 3, step: 1 }} value={opts.smoothness} onChange={(v) => set('smoothness', Math.round(v))} />
                <NumberField label={t('Fill angle')} decimals={0} limit={{ min: -180, max: 180, step: 5, unit: '°' }} value={opts.fillAngle} onChange={(v) => set('fillAngle', v)} />
                <NumberField label={t('Satin up to')} limit={{ min: 1, max: 10, step: 0.5, unit: 'mm' }} value={opts.satinMaxWidthMm} onChange={(v) => set('satinMaxWidthMm', v)} />
                <NumberField label={t('Overlap')} limit={{ min: 0, max: 1.5, step: 0.05, unit: 'mm' }} value={opts.overlapMm} onChange={(v) => set('overlapMm', v)} />
              </div>
              <label className="check">
                <input type="checkbox" checked={opts.allowSatin} onChange={(e) => set('allowSatin', e.target.checked)} /> {t('Use satin on narrow areas')}
              </label>
            </div>
            {hasObjects && (
              <div className="section" style={{ padding: '10px 0', borderBottom: 0 }}>
                <label className="check">
                  <input type="radio" checked={mode === 'replace'} onChange={() => setMode('replace')} /> {t('Replace the existing design')}
                </label>
                <label className="check">
                  <input type="radio" checked={mode === 'add'} onChange={() => setMode('add')} /> {t('Add to the design')}
                </label>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="modal-foot">
        {progress ? (
          <div style={{ flex: 1, minWidth: 200 }}>
            <div className="hint" style={{ margin: '0 0 4px' }}>
              {progress.phase} … {Math.round(progress.f * 100)} %
            </div>
            <div className="progress" role="progressbar" aria-valuenow={Math.round(progress.f * 100)} aria-valuemin={0} aria-valuemax={100}>
              <div style={{ width: `${progress.f * 100}%` }} />
            </div>
          </div>
        ) : (
          <button className="btn ghost" onClick={onBack}>
            {t('Choose another image')}
          </button>
        )}
        <span className="spacer" />
        {progress ? (
          <button className="btn" onClick={() => job.current?.cancel()}>
            {t('Cancel digitizing')}
          </button>
        ) : (
          <>
            <button className="btn" onClick={useAsBackdrop} title={t('Add the image as a background and draw the shapes yourself')}>
              {t('Manual: use as background')}
            </button>
            <button className="btn primary" onClick={run} disabled={!analysis || analyzing || !edit || edit.hex.every((_, i) => edit.excluded.has(i))}>
              {t('Digitize automatically')}
            </button>
          </>
        )}
      </div>
    </>
  );
}

function PaletteEditor({ edit, setEdit, onRecolor }: { edit: PaletteEdit; setEdit: (f: (e: PaletteEdit | null) => PaletteEdit | null) => void; onRecolor: (hex: string[]) => void }) {
  const roots = edit.order.filter((i) => edit.remap[i] === i);
  const moveOrder = (i: number, d: -1 | 1) =>
    setEdit((e) => {
      if (!e) return e;
      const visible = e.order.filter((k) => e.remap[k] === k);
      const pos = visible.indexOf(i);
      const j = pos + d;
      if (j < 0 || j >= visible.length) return e;
      [visible[pos], visible[j]] = [visible[j], visible[pos]];
      return { ...e, order: [...visible, ...e.order.filter((k) => e.remap[k] !== k)] };
    });
  return (
    <>
      <p className="hint">{t('The order below is the sewing order of the colours. Change a colour to move the borders between the colour areas.')}</p>
      <ul className="palette-list" style={{ marginTop: 6 }}>
        {roots.map((i, pos) => {
          const merged = edit.hex.map((_, k) => k).filter((k) => k !== i && edit.remap[k] === i);
          const share = edit.share[i] + merged.reduce((s, k) => s + edit.share[k], 0);
          const excluded = edit.excluded.has(i);
          return (
            <li key={i} className={'palette-item' + (excluded ? ' excluded' : '')}>
              <input
                type="color"
                className="swatch-input"
                value={edit.hex[i]}
                aria-label={t('Colour {n}', { n: pos + 1 })}
                onChange={(e) => onRecolor(edit.hex.map((h, k) => (k === i ? e.target.value : h)))}
              />
              <div style={{ minWidth: 0 }}>
                <div className="row" style={{ gap: 4 }}>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{edit.hex[i]}</span>
                  <span className="hint" style={{ margin: 0 }}>{(share * 100).toFixed(1)} %</span>
                </div>
                {merged.length > 0 && (
                  <div className="row" style={{ gap: 3 }}>
                    <span className="hint" style={{ margin: 0 }}>+</span>
                    {merged.map((k) => (
                      <button
                        key={k}
                        className="btn small icon ghost"
                        title={t('Separate again')}
                        onClick={() => setEdit((e) => (e ? { ...e, remap: e.remap.map((r, x) => (x === k ? k : r)) } : e))}
                      >
                        <span className="swatch" style={{ background: edit.hex[k] }} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="row" style={{ gap: 2 }}>
                <button className="btn small icon" aria-label={t('Sew earlier')} disabled={pos === 0} onClick={() => moveOrder(i, -1)}>
                  <IconUp size={12} />
                </button>
                <button className="btn small icon" aria-label={t('Sew later')} disabled={pos === roots.length - 1} onClick={() => moveOrder(i, 1)}>
                  <IconDown size={12} />
                </button>
                <select
                  className="input"
                  style={{ width: 92, height: 24 }}
                  aria-label={t('Action for the colour')}
                  value=""
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === 'toggle') setEdit((x) => (x ? { ...x, excluded: toggle(x.excluded, i) } : x));
                    else if (v.startsWith('merge:')) {
                      const target = Number(v.slice(6));
                      setEdit((x) => (x ? { ...x, remap: x.remap.map((r) => (r === i ? target : r)) } : x));
                    }
                  }}
                >
                  <option value="">{t('More…')}</option>
                  <option value="toggle">{excluded ? t('Sew this colour') : t('Do not sew (remove)')}</option>
                  {roots
                    .filter((k) => k !== i)
                    .map((k) => (
                      <option key={k} value={`merge:${k}`}>
                        {t('Merge with {colour}', { colour: edit.hex[k] })}
                      </option>
                    ))}
                </select>
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function toggle(s: Set<number>, i: number): Set<number> {
  const n = new Set(s);
  if (n.has(i)) n.delete(i);
  else n.add(i);
  return n;
}

function QuantPreview({ analysis, edit }: { analysis: Analysis; edit: PaletteEdit }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    c.width = analysis.width;
    c.height = analysis.height;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const data = ctx.createImageData(analysis.width, analysis.height);
    const rgb = edit.hex.map((_, i) => {
      const root = edit.remap[i];
      return edit.excluded.has(root) ? null : hexRgb(edit.hex[root]);
    });
    for (let i = 0; i < analysis.labels.length; i++) {
      const l = analysis.labels[i];
      const c3 = l >= 0 ? rgb[l] : null;
      if (!c3) continue;
      data.data[i * 4] = c3[0];
      data.data[i * 4 + 1] = c3[1];
      data.data[i * 4 + 2] = c3[2];
      data.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(data, 0, 0);
  }, [analysis, edit]);
  return <canvas ref={ref} aria-label={t('Preview of the colour separation')} />;
}

/** Image preview with a draggable crop rectangle. */
function CropView({ url, width, height, crop, onCrop }: { url: string; width: number; height: number; crop: CropRect; onCrop: (c: CropRect) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const [disp, setDisp] = useState({ w: 0, h: 0, ox: 0, oy: 0 });
  const drag = useRef<{ kind: string; sx: number; sy: number; c: CropRect } | null>(null);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const update = () => {
      const s = Math.min(el.clientWidth / width, el.clientHeight / height);
      setDisp({ w: width * s, h: height * s, ox: (el.clientWidth - width * s) / 2, oy: (el.clientHeight - height * s) / 2 });
    };
    const ro = new ResizeObserver(update);
    ro.observe(el);
    update();
    return () => ro.disconnect();
  }, [width, height]);
  const s = disp.w / width || 1;
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = (e.clientX - d.sx) / s, dy = (e.clientY - d.sy) / s;
    let { x, y, width: w, height: h } = d.c;
    if (d.kind === 'move') {
      x = Math.max(0, Math.min(width - w, x + dx));
      y = Math.max(0, Math.min(height - h, y + dy));
    } else {
      if (d.kind.includes('w')) {
        const nx = Math.max(0, Math.min(x + w - 8, x + dx));
        w += x - nx;
        x = nx;
      }
      if (d.kind.includes('e')) w = Math.max(8, Math.min(width - x, w + dx));
      if (d.kind.includes('n')) {
        const ny = Math.max(0, Math.min(y + h - 8, y + dy));
        h += y - ny;
        y = ny;
      }
      if (d.kind.includes('s')) h = Math.max(8, Math.min(height - y, h + dy));
    }
    onCrop({ x: Math.round(x), y: Math.round(y), width: Math.round(w), height: Math.round(h) });
  };
  const start = (kind: string) => (e: React.PointerEvent) => {
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);
    drag.current = { kind, sx: e.clientX, sy: e.clientY, c: crop };
  };
  const hs: [string, React.CSSProperties][] = [
    ['nw', { left: -6, top: -6, cursor: 'nwse-resize' }],
    ['ne', { right: -6, top: -6, cursor: 'nesw-resize' }],
    ['sw', { left: -6, bottom: -6, cursor: 'nesw-resize' }],
    ['se', { right: -6, bottom: -6, cursor: 'nwse-resize' }],
  ];
  return (
    <div className="preview-box" ref={box} onPointerMove={onMove} onPointerUp={() => (drag.current = null)}>
      <span className="label chip" style={{ zIndex: 2 }}>{t('Original · drag to crop')}</span>
      <img src={url} alt={t('Original image')} style={{ position: 'absolute', left: disp.ox, top: disp.oy, width: disp.w, height: disp.h, maxWidth: 'none', maxHeight: 'none' }} draggable={false} />
      <div className="crop-layer" style={{ left: disp.ox, top: disp.oy, width: disp.w, height: disp.h, overflow: 'hidden' }}>
        <div
          className="crop-rect"
          style={{ left: crop.x * s, top: crop.y * s, width: crop.width * s, height: crop.height * s }}
          onPointerDown={start('move')}
          aria-label={t('Crop area')}
        >
          {hs.map(([k, st]) => (
            <span key={k} className="h" style={st} onPointerDown={start(k)} />
          ))}
        </div>
      </div>
    </div>
  );
}
